import { Preferences } from '@capacitor/preferences'
import { platform } from '@utils/capacitor-helper'

const NUDGE_DISMISSAL_PREFIX = 'nudge_dismissed_'

interface NudgeDismissalData {
    dismissedAt: string // ISO timestamp, kept for reference
    dismissedCycle: number
}

/**
 * Get the storage key for a specific group's nudge dismissal
 */
function getStorageKey(groupId: string): string {
    return `${NUDGE_DISMISSAL_PREFIX}${groupId}`
}

/**
 * Save nudge dismissal data for a group
 */
export async function saveNudgeDismissal(groupId: string, cycleIndex: number): Promise<void> {
    const data: NudgeDismissalData = {
        dismissedAt: new Date().toISOString(),
        dismissedCycle: cycleIndex
    }
    const key = getStorageKey(groupId)
    const value = JSON.stringify(data)

    if (platform === 'web') localStorage.setItem(key, value)
    else await Preferences.set({ key, value })
}

/**
 * Check if the nudge should be shown for a group based on dismissal cycles
 */
export async function isNudgeDismissed(groupId: string, currentCycleIndex: number): Promise<boolean> {
    const key = getStorageKey(groupId)
    let storedValue: string | null = null

    if (platform === 'web') storedValue = localStorage.getItem(key)
    else {
        const result = await Preferences.get({ key })
        storedValue = result.value
    }

    if (!storedValue) return false

    try {
        const data: NudgeDismissalData = JSON.parse(storedValue)
        
        // If the stored dismissal is for the current cycle (or a future one??), keep it dismissed.
        // If we have moved to a new cycle (current > dismissed), show it again.
        return data.dismissedCycle === currentCycleIndex
    } catch {
        // If parsing fails, allow the nudge to show
        return false
    }
}

/**
 * Clear nudge dismissal data for a group (useful for testing or reset scenarios)
 */
export async function clearNudgeDismissal(groupId: string): Promise<void> {
    const key = getStorageKey(groupId)

    if (platform === 'web') localStorage.removeItem(key)
    else await Preferences.remove({ key })
}
